<template>
  <div class="team-list-container">
    <SettingsListSkeletonLoader v-if="listLoading" class="list-skeleton-loader" />
    <div v-else>
      <TeamTable
        ref="teamTable"
        :can-team-admin="checkRbacPermissions(['manageTeam'])"
        :integrated="integrated"
        :team-list="displayableTeamList"
        :progress-bar="progressBar"
        :loading="listLoading"
        @update-teams="updateTeams"
        @set-pagination="updatePagination"
        @show-delete-modal="showDeleteModal"
        @send-onboarding-emails="sendMassInvite"
        @teams-tracking-event="handleTrackingEvent"
      />
      <DeleteModal
        document-type="user"
        :show="deleteModalVisible"
        :name="userName"
        @close="deleteModalVisible = false"
        @delete="deleteItem(userId)"
      />
    </div>
    <InviteModal
      v-if="!integrated || allowIntegratedUserInvite"
      :show="show.settings"
      :is-valid="isInviteFormValid"
      :loading="modalLoading"
      :form="{
        values: inviteValues,
        errors: inviteErrors,
        errorMessages: inviteErrorMessages,
      }"
      @close="setModal({ field: 'settings', value: false })"
      @submit="sendOnboardingEmails"
      @update:model-value="setInviteFormValues($event)"
      @change="setInviteIsValidated($event)"
    />
  </div>
</template>

<script setup>
import InviteModal from '@/components/settings/team/InviteModal.vue';
import SettingsListSkeletonLoader from '@/components/shared/SkeletonLoaders/settings/SettingsListSkeletonLoader.vue';
import TeamTable from '@/components/settings/team/TeamTable.vue';
import DeleteModal from '@/components/shared/DeleteModal.vue';
import { useAppLoading } from '@/stores/app/loading';
import { storeToRefs } from 'pinia';
import { useAppAuthData } from '@/stores/app/authData';
import { useAppPagination } from '@/stores/app/pagination';
import { useSettingsTeam } from '@/stores/settings/team';
import { useTracking } from '@/stores/tracking/tracking';
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useModals } from '@/stores/modals/modals';
import { useSettingsTeamInviteForm } from '@/stores/settings/teamInviteForm';
import { subscribeToFilterUpdates, unsubscribeToFilterUpdates } from '@/helpers/filterDispatcher';
import { useFilters } from '@/stores/filters/filters';
import { useSettingsIntegrations } from '@/stores/settings/integrations';

const route = useRoute();
const router = useRouter();

const emit = defineEmits(['track-page-load']);

const { listLoading, modalLoading } = storeToRefs(useAppLoading());
const { checkRbacPermissions } = useAppAuthData();
const { integrated } = storeToRefs(useAppAuthData());
const { displayableTeamList } = storeToRefs(useSettingsTeam());
const { show } = storeToRefs(useModals());
const {
  isFormValid: isInviteFormValid,
  values: inviteValues,
  errors: inviteErrors,
  errorMessages: inviteErrorMessages,
} = storeToRefs(useSettingsTeamInviteForm());
const { totalPages } = storeToRefs(useAppPagination());

const {
  updateTeamsView,
  sendOnboardingEmails,
  deleteUser,
  loadTeam,
} = useSettingsTeam();
const { updatePagination } = useAppPagination();
const { setModal } = useModals();
const {
  setFormValues: setInviteFormValues,
  setIsValidated: setInviteIsValidated,
} = useSettingsTeamInviteForm();
const { setFilterConfig } = useFilters();

const { allowIntegratedUserInvite } = storeToRefs(useSettingsIntegrations());

const progressBar = ref(false);
const userName = ref(null);
const userId = ref(null);
const deleteModalVisible = ref(false);

onMounted(async () => {
  subscribeToFilterUpdates(loadTeam);

  emit('track-page-load', { page: 'Platform Permissions' });

  setFilterConfig({
    module: 'settings/team',
    datepicker: false,
    showMyJobs: false,
    subMenuType: null,
  });

  await loadTeam({
    firstLoad: true,
    router,
    routeQuery: route.query,
    page: route.query?.page || 1,
    selectedSortOption: route.query?.sort || 'name',
    searchTerm: route.query?.q || '',
  });

  let page = Number(route.query?.page) || 1;

  const changeQuery = !route.query?.page || page > totalPages;

  if (page > totalPages) page = totalPages;

  if (changeQuery) {
    router.replace({ query: { ...route.query, page } }).catch(() => false);
  }
});

onBeforeUnmount(() => {
  unsubscribeToFilterUpdates();

  updatePagination({ page: 1, totalPages: 1 });
});

const handleTrackingEvent = ({ action, args }) => {
  useTracking()[action](args);
};

const updateTeams = (event) => {
  handleTrackingEvent({ action: 'teamsViewUpdated', args: event });
  updateTeamsView({
    ...event,
    routeQuery: route.query,
  });
};

const showDeleteModal = ({ item }) => {
  userName.value = item.name;
  userId.value = item.id;
  deleteModalVisible.value = true;
};

const sendMassInvite = (event) => {
  handleTrackingEvent({ action: 'massInviteSent', args: { selectedCount: event } });
  return sendOnboardingEmails(event);
};

const deleteItem = async (id) => {
  deleteModalVisible.value = false;
  progressBar.value = true;

  try {
    await deleteUser({ id, name: userName.value });
  } finally {
    progressBar.value = false;
  }
};
</script>

<style scoped lang="less">
.team-list-container {
  min-width: 1092px;
  .v-card-header {
    background-color: transparent;
    box-shadow: none !important;
  }
  .v-card-title {
    margin: 0;
    padding: 0;
  }
  .disabled {
    pointer-events: none;
    opacity: 0.4;
  }
  .invite-button {
    margin: 16px 0;
    padding: 0 40px;
    :deep(.v-btn__content) {
      font-weight: 400;
    }
    &.v-btn {
      background-color: @white !important;
    }
    &:hover {
      background-color: @text-brand !important;

      :deep(.v-btn__overlay) {
        display: none;
      }
    }
    &:hover :deep(.v-btn__content) {
      color: @white;
    }
  }
}

.list-skeleton-loader {
  border-radius: 8px;
}
</style>
